import { definitionFor } from './held-definition';
import { awaitEntityCompileReady } from '../shared/entity-compile-ready';
import { startActionTiming, markActionTiming, bindActionTiming, endActionTiming } from '../shared/action-timing';
import { withLogParticipants } from '../shared/log-participant-snapshot';
import { planSelfStatusSave } from '../janus-engine/actions/self-status-save.js';
import { ftClockCampaignField, type CampaignClockCols } from '../shared/campaign-clock.js';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { buildAppliedFrame } from '../janus-engine/transport/applied-frame';
// action-truth.ts — A1 (Dodge): the client-truth USE-ABILITY transport adapter + WS subscriber.
//
// Mirrors movement-truth.ts (slice 0) on the shared per-actor outbox: the pure resolver
// (janus-engine/actions/self-status.ts) decides, the row is mutated in place and rendered at once, the
// browser pushes its applied result before action_truth_commit_inc.php saves it.
// A handled Dodge NEVER reaches use_ability_inc.php, and a
// refusal the resolver can prove locally is shown without a request either.
//
// Reconciliation rule (Codex review 2026-09-06, A1/A2 findings 1): a rejected or corrected operation
// REVERTS ONLY ITS OWN EFFECTS (its status entry + the skill lines that cite it) and ADOPTS the
// authoritative AP — never a whole-row snapshot, which would erase a later committed action.
import {
  applyActionTruth, resolveSelfStatusAction, SELF_STATUS_SCHEMA,
  type ActionTruthCommand, type ActionTruthFrame, type SelfStatusReaders,
} from '../janus-engine/actions/self-status.js';
import {
  applyApsScalar, MovementTruthRejected, MovementTruthTerminal,
  type ActorTruthCommandBase, type MovementTruthAcceptedResult, type MovementTruthRejectedResult, type MovementTruthRow, type MovementTruthTerminalInfo,
} from '../janus-engine/movement/client-truth-commit.js';
import { creatureIsInActiveCombat } from '../roll-init/client-combat-membership.js';
import { encounterField, encounterCreatureField, type CasterArr } from '../shared/array-types.js';
import { classifyRefusal } from '../shared/revert-on-refusal.js';
import { handleCasterUpdate } from './caster-update.js';
import { fetchAndCacheObject } from './object-cache.js';
import { audioTiers, peekAudioCatalog, pickActionSound } from '../shared/audio-catalog.js';
import { actionVoiceTiers, pageRollSoundContext } from '../shared/roll-sound-route.js';
import { playActionSound } from './roll-audio-mixer.js';
import {
  actorEncounterFor, actorHasStatus, actorOutbox, actorRowFor, admitProvisional, bankConfirmedRev, confirmedApsFor, confirmedRevFor,
  noteFrameSeen, noteOwnOperation, pendingFor, reconcileActorRow, registerTruthSchema,
} from './movement-truth.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const DODGE = '62323E36-5B5E-0114-2750-ED864773B062';
/** Abilities whose definition may be FETCHED when the row's catalog lacks it (universal actions the
 * compile interns since 2026-09-06; older warm blobs predate that). Everything else must already be
 * local or another native lane must handle the click. */
const FETCHABLE_DEFINITIONS = new Set([DODGE]);
const ENDPOINT = '/options/game/actions/action_truth_commit_inc.php';

/** What an in-flight operation changed, by operation id — enough to revert exactly that operation
 * (own commits AND peers' provisional frames) without touching anything committed since. */
interface Pending { statusId: string | null; connectedId: string; aps: number; apsUsed: number | null; encounterId: string; actorId: string; rev: number }
const pending = new Map<string, Pending>();
const localApplications = new Set<string>();
function remember(command: ActorTruthCommandBase, row: Row, frame: ActionTruthFrame, rev: number): void {
  const ec = asRow(row.encounter_creatures) ?? {};
  pending.set(command.operation_id, { statusId: frame.statuses_target_entry ? upper(frame.statuses_target_entry.status_id) : null, connectedId: upper(frame.statuses_target_entry?.status_connected_id), aps: Number(ec.aps ?? 0), apsUsed: ec.aps_used === undefined ? null : Number(ec.aps_used), encounterId: upper(command.encounter_id), actorId: upper(command.actor_id), rev });
  if (pending.size > 256) pending.delete(pending.keys().next().value!);
}
function newerPending(command: ActorTruthCommandBase, rev: number): boolean {
  return [...pending.values()].some(p => p.encounterId === upper(command.encounter_id) && p.actorId === upper(command.actor_id) && p.rev > rev);
}

function readers(row: Row, encounter: Row): SelfStatusReaders {
  const skillCatalog = (): Record<string, unknown> | undefined => (window as unknown as { SKILL_CATALOG?: Record<string, unknown> }).SKILL_CATALOG;
  const parent = upper(encounterField(encounter, 'encounter_master_parent_id'));
  const held = window.FateTide as unknown as { campaignCache?: Map<string, Row>; currentCampaignArr?: Row } | undefined;
  const campaign = [held?.campaignCache?.get(parent), held?.currentCampaignArr]
    .find(candidate => candidate && upper(ftClockCampaignField(candidate, 'campaign_id')) === parent) ?? null;
  return {
    campaign,
    campaignClock: () => campaign ? (window as unknown as { ftCampaignClock?: { getDerived?: () => { cols?: CampaignClockCols } | null } }).ftCampaignClock?.getDerived?.()?.cols ?? null : null,
    value: (target, key) => {
      if (target === encounter) { const bucket = asRow(encounter.encounters); return bucket && key in bucket ? bucket[key] : encounter[key]; }
      return ftLayerValue(target, key);
    },
    entries: (target, key) => ftLayerEntries(target, key),
    definition: id => definitionFor(row, encounter, id),
    statuses: target => ftCreatureStatuses(target, 'target') as Row[],
    name: id => { const def = definitionFor(row, encounter, id); return String(ftItemsCol(def, 'item_name') ?? ftItemsCol(asRow(asRow(row.catalog)?.[upper(id)]), 'item_name') ?? id); },
    inCombat: (enc, target) => creatureIsInActiveCombat(enc, target as never),
    skillVariable: id => { const entry = asRow(skillCatalog()?.[id] ?? skillCatalog()?.[upper(id)]); const v = String(entry?.variable_name ?? ''); return v === '' ? null : v; },
    hasStatus: (target, name) => actorHasStatus(target, name),
    uuid: () => crypto.randomUUID().toUpperCase(),
  };
}
function render(command: ActorTruthCommandBase, row: MovementTruthRow): void {
  handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  if (localApplications.delete(command.operation_id)) {
    const rev = command.expected_rev + 1;
    // Native action display deltas are consumed by handleActionTruth on peers.
    // Keep the raw bases aligned without re-running that status projection.
    const c = command as unknown as ActionTruthCommand;
    const applied_frame = buildAppliedFrame({ operation_id: command.operation_id, encounter_id: command.encounter_id,
      actor_id: command.actor_id, rev, member_revs: { [command.actor_id]: rev },
      rows: [{ table: 'encounter_creatures', row_id: command.actor_id, partial: true,
        row: { ...c.writes.encounter_creatures, movement_rev: rev, movement_last_operation_id: command.operation_id } },
        ...c.writes.statuses_insert.map(status => ({ table: 'statuses', row_id: String(status.status_id), row: status }))],
      computed: {}, new_entities: [], deletes: [] }, Infinity);
    window.ftPushAppliedTruth?.({ type: 'janus_action_truth', command: { ...command, rev }, applied_frame });
  }
  window.dispatchEvent(new CustomEvent('ft:action-truth-applied', { detail: { command } }));
}
const frameOf = (command: ActorTruthCommandBase): ActionTruthFrame => (command as unknown as ActionTruthCommand).frame;
const abilityOf = (command: ActorTruthCommandBase): string => upper((command as unknown as ActionTruthCommand).ability_id);
function definitionOrFetch(row: Row, encounter: Row | null, abilityId: string, command: ActorTruthCommandBase): Row | null {
  const def = definitionFor(row, encounter, abilityId);
  if (!def && FETCHABLE_DEFINITIONS.has(abilityId)) {
    // A peer whose warm blob predates the catalog grant: fetch once, then re-render the icon.
    void fetchAndCacheObject(abilityId).then(fetched => {
      const current = actorRowFor(command.encounter_id, command.actor_id);
      if (!fetched || !current) return;
      const catalog = (current.catalog ??= {}) as Row;
      if (!asRow(catalog[abilityId])?.item_layer_types) catalog[abilityId] = fetched as unknown as Row;
      render(command, current);
    });
  }
  return def;
}

/** Remove exactly one operation's effects: its status entry (both projections), the skill breakdown
 * lines citing it, and — when authority supplied it — the AP scalars. Anything else on the row stays. */
function revert(row: Row, statusIds: string[], aps: number | null, apsUsed: number | null, rev?: number): void {
  const remove = new Set(statusIds.map(upper).filter(Boolean));
  if (remove.size) {
    const statuses = asRow(row.statuses);
    for (const mode of ['target', 'caster']) {
      const list = statuses?.[mode];
      if (Array.isArray(list)) statuses![mode] = list.filter(entry => !remove.has(upper(asRow(entry)?.status_id)));
    }
    const skills = asRow(asRow(asRow(row.computed)?.creature)?.skills) ?? {};
    for (const entry of Object.values(skills)) {
      const skill = asRow(entry); const breakdown = asRow(skill?.adv_dis_breakdown);
      if (!skill || !Array.isArray(breakdown?.lines)) continue;
      const lines = (breakdown!.lines as Row[]).filter(line => !remove.has(upper(line.href_id)));
      if (lines.length === breakdown!.lines.length) continue;
      const stacks = lines.reduce((sum, line) => sum + Number(line.value ?? 0), 0);
      if (stacks === 0) delete skill.adv_dis_stacks; else skill.adv_dis_stacks = stacks;
      if (lines.length) skill.adv_dis_breakdown = { lines }; else delete skill.adv_dis_breakdown;
    }
  }
  const ec = (row.encounter_creatures ??= {}) as Row;
  if (aps !== null) applyApsScalar(ec, aps, apsUsed);
  if (Number.isInteger(rev) && rev! >= 0) ec.movement_rev = rev;
}
/** Promote only this cast's existing entry, including SQL's revived id. The connected id binds
 * the cast epoch: a late answer must not stamp a later revival of the same saved SQL row.
 * Never inserts a missing/expired effect or replaces a newer skill snapshot. */
function stampEntry(row: Row, entry: Row | null, proposedIds: string[]): void {
  if (!entry) return;
  const savedId = upper(entry.status_id), connectedId = upper(entry.status_connected_id);
  if (!savedId || !connectedId) return;
  const ids = new Set([...proposedIds.map(upper), savedId]);
  const matches = (candidate: unknown): boolean => {
    const e = asRow(candidate);
    return !!e && ids.has(upper(e.status_id)) && upper(e.status_connected_id) === connectedId
      && upper(e.status_target_id) === upper(entry.status_target_id) && upper(e.status_item_id) === upper(entry.status_item_id);
  };
  const statuses = asRow(row.statuses);
  const renamed = new Set<string>();
  for (const mode of ['target', 'caster']) {
    const list = statuses?.[mode];
    if (!Array.isArray(list)) continue;
    let installed = false;
    statuses![mode] = list.flatMap(existing => {
      if (!matches(existing)) return [existing];
      const id = upper(asRow(existing)?.status_id);
      if (id !== savedId) renamed.add(id);
      if (installed) return [];
      installed = true;
      return [{ ...(existing as Row), ...entry }];
    });
  }
  const skills = asRow(asRow(asRow(row.computed)?.creature)?.skills) ?? {};
  for (const skill of Object.values(skills)) {
    const lines = asRow(asRow(skill)?.adv_dis_breakdown)?.lines;
    if (!Array.isArray(lines)) continue;
    for (const line of lines) if (asRow(line) && renamed.has(upper(line.href_id))) line.href_id = savedId;
  }
}

async function post(command: ActorTruthCommandBase): Promise<unknown> {
  const body = withLogParticipants(command);
  const response = await saveAppliedTruth(ENDPOINT, command, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(20000) });
  let result: Row | null = null;
  try { result = await response.json() as Row; } catch { result = null; }
  if (!response.ok && result?.error === 'stale' && Number.isInteger(result.rev)) {
    // The outbox contract is movement-shaped; an action rejection carries no position, so hand it the
    // row's current one (never read back by the action handlers). `aps` is the authoritative value.
    const row = actorRowFor(command.encounter_id, command.actor_id);
    const position = { x_pos: Number(row?.position?.x_pos ?? 0), y_pos: Number(row?.position?.y_pos ?? 0), z_pos: Number(row?.position?.z_pos ?? 0) };
    throw new MovementTruthRejected({ ok: false, error: 'stale', rev: Number(result.rev), position, ...(Number.isInteger(result.aps) ? { aps: result.aps } : {}) } as MovementTruthRejectedResult);
  }
  if (response.ok && result?.ok === true) return result;
  const message = String(result?.error ?? (response.statusText || 'Action delivery failed'));
  if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, message);
  throw new Error(message);
}
/** Shared-scalar rule (Codex M1c review A2, findings 6 + 8): after an operation's OWN effects are reverted, the AP
 * on the row comes from the confirmed snapshot — never from a rollback capture (which may hold a preceding
 * optimistic walk or a peer's provisional) and never from a raw answer older than what is confirmed. The
 * revision is stamped from the snapshot only when nothing optimistic is pending. */
function restoreSharedScalars(command: ActorTruthCommandBase, row: MovementTruthRow, native?: ActionTruthFrame): void {
  applyApsScalar(row.encounter_creatures, confirmedApsFor(command.encounter_id, command.actor_id, row), native?.aps_used);
  reconcileActorRow(command.encounter_id, command.actor_id, row);
}
/** The status ids an operation installed: from its IMMUTABLE command first (the pending map is bounded and may have
 * evicted the record — Codex M1c review A3), the remembered record second. */
function ownStatusIds(command: ActorTruthCommandBase, own: Pending | undefined): string[] {
  const fromCommand = upper((frameOf(command) as ActionTruthFrame | undefined)?.statuses_target_entry?.status_id);
  return [...new Set([fromCommand, own?.statusId ?? ''].filter(Boolean))] as string[];
}
/** A saved id is reusable after expiry. A late rejection/correction may retire its original cast,
 * never a newer cast now carrying that id. Without the provisional's connection, keep an installed
 * entry; explicit ids can still remove orphaned skill lines. */
function removalIds(row: Row, command: ActorTruthCommandBase, own: Pending | undefined, extra: string[] = []): string[] {
  const connectedId = upper(frameOf(command)?.statuses_target_entry?.status_connected_id) || own?.connectedId || '';
  const statuses = asRow(row.statuses);
  const installed = ['target', 'caster'].flatMap(mode => Array.isArray(statuses?.[mode]) ? statuses![mode] as unknown[] : []).map(asRow);
  return [...extra, ...ownStatusIds(command, own)].filter(id => !installed.some(entry => upper(entry?.status_id) === upper(id)
    && (!connectedId || upper(entry?.status_connected_id) !== connectedId)));
}
function accepted(command: ActorTruthCommandBase, result: MovementTruthAcceptedResult): void {
  // after this answer is stamped on the row; `revived` = SQL kept an EXISTING row (the unique target + item key) instead of the proposed one
  const savedId = upper(asRow(asRow((result as unknown as Row).frame)?.statuses_target_entry)?.status_id);
  queueMicrotask(() => settleSelfStatus(command.operation_id, true, savedId));
  const row = actorRowFor(command.encounter_id, command.actor_id);
  if (!row) return;
  const saved = asRow((result as unknown as Row).frame);
  const banked = confirmedRevFor(command.encounter_id, command.actor_id, row);
  const own = pending.get(command.operation_id);
  // The shared confirmed snapshot learns the saved AP too (M1c): a later movement restore must not resurrect it.
  bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev, Number.isInteger(saved?.aps) ? { aps: Number(saved!.aps) } : {});
  pending.delete(command.operation_id);
  if (!saved) return;
  const stamped = asRow(saved.statuses_target_entry);
  const optimistic = Number(row.encounter_creatures.movement_rev ?? 0);
  stampEntry(row as Row, stamped, ownStatusIds(command, own));
  const laterPending = newerPending(command, result.rev);
  if (result.rev >= banked && result.rev >= optimistic && !laterPending) {
    applyActionTruth(row as Row, saved as unknown as ActionTruthFrame, undefined, definitionOrFetch(row as Row, actorEncounterFor(command.encounter_id), abilityOf(command), command));
    restoreSharedScalars(command, row, saved as unknown as ActionTruthFrame);
  } else if (!laterPending && pendingFor(command.encounter_id, command.actor_id) === 0) {
    restoreSharedScalars(command, row);
  }
  render(command, row);
}
function rejected(command: ActorTruthCommandBase, result: MovementTruthRejectedResult): void {
  settleSelfStatus(command.operation_id, false);
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  if (row) {
    // Revert this operation's own effects; bank the authoritative AP the answer supplied (per field — an answer older
    // than a newer confirmation changes nothing); then the row's shared scalars come from the merged snapshot.
    revert(row as Row, removalIds(row as Row, command, own), null, null);
    const answered = (result as unknown as Row).aps;
    bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev, Number.isInteger(answered) ? { aps: Number(answered) } : {});
    restoreSharedScalars(command, row);
    render(command, row);
  }
  window.dispatchEvent(new CustomEvent('ft:action-truth-reconciled', { detail: { command, authoritative: { rev: result.rev } } }));
}
function failed(command: ActorTruthCommandBase, info: MovementTruthTerminalInfo): void {
  settleSelfStatus(command.operation_id, false);
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  if (row) { revert(row as Row, removalIds(row as Row, command, own), null, null); restoreSharedScalars(command, row); render(command, row); }
  // R-121: every refusal is visible on the page that drew it (the Dodge reverted silently before)
  const refusal = classifyRefusal({ status: info.status, body: { error: info.error } });
  if (typeof displayWarning === 'function') displayWarning(refusal?.reason === 'permission' ? 'You do not have permission to act as this creature. The action was undone.'
    : 'The action was refused (' + info.error + '). It was undone.');
  window.dispatchEvent(new CustomEvent('ft:action-truth-refused', { detail: { command, info, refusal } }));
}
registerTruthSchema(SELF_STATUS_SCHEMA, {
  apply: (row, command, rev) => {
    remember(command, row as Row, frameOf(command), rev);
    applyActionTruth(row as Row, frameOf(command), rev, definitionOrFetch(row as Row, actorEncounterFor(command.encounter_id), abilityOf(command), command));
    localApplications.add(command.operation_id);
  },
  render, commit: post, accepted, rejected, failed,
  staleMessage: 'Your Dodge was rejected: this creature changed elsewhere. Action points and status restored.',
});

export type SelfStatusCommitOutcome = 'handled' | 'refused' | null;
/** Null leaves an unconverted ability / caster / situation to another native lane before mutation.
 * 'refused' shows the native gate's message; 'handled' means mutated, rendered and queued. */
export async function tryCommitSelfStatus(input: { encounterId: string; actorId: string; abilityId: string; rollPrivate?: number }): Promise<SelfStatusCommitOutcome> {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), abilityId = upper(input.abilityId);
  const timing = abilityId === DODGE ? startActionTiming('dodge') : undefined;
  try {
  if (!await awaitEntityCompileReady([encounterId, actorId], actorId)) return 'refused';
  markActionTiming(timing, 'decide_ms');
  let encounter = actorEncounterFor(encounterId), row = actorRowFor(encounterId, actorId);
  if (!encounter || !row) return null;
  if (!definitionFor(row as Row, encounter, abilityId)) {
    if (!FETCHABLE_DEFINITIONS.has(abilityId)) return null;
    await fetchAndCacheObject(abilityId); // one GET per session; interned in objectCache
    // Re-acquire after the await: a cache refresh may have replaced both objects meanwhile.
    encounter = actorEncounterFor(encounterId); row = actorRowFor(encounterId, actorId);
    if (!encounter || !row || !definitionFor(row as Row, encounter, abilityId)) return null;
  }
  // The row's revision is confirmed + pending: every legacy install banked itself (bankLegacyRowTruth, R1).
  confirmedRevFor(encounterId, actorId, row);
  const userId = upper(window.FateTide?.user?.id), isGM = window.FateTide?.user?.isGM === true;
  let scope;
  try { scope = resolveSelfStatusAction({ encounterId, actorId, abilityId, userId, isGM, rollPrivate: input.rollPrivate ?? 0 }, encounter, row as Row, readers(row as Row, encounter)); }
  catch (error) { window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { reason: String(error) } })); return null; }
  if (!scope.supported) {
    if (scope.refused) { if (typeof displayWarning === 'function') displayWarning(scope.message); return 'refused'; }
    window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { reason: scope.reason, abilityId } }));
    return null;
  }
  const outbox = actorOutbox(encounterId, actorId);
  const command = scope.command as unknown as ActorTruthCommandBase;
  // The actor's own sound, picked now from the ability's assets and played the instant the status applies (owner
  // 2026-09-10: not tied to a saved roll) — the route its `<type>_applied` row carried: its own pool, then the die.
  // A no-op use (already active) logs no such row and makes no sound. action_truth_commit_inc.php claims the row.
  let sound: ReturnType<typeof pickActionSound> = null;
  try {
    // Public actions carry the browser's picked sound on the frame.
    if (scope.command.writes.log.key === 'ability_applied' && (input.rollPrivate ?? 0) === 0) {
      const def = definitionFor(row as Row, encounter, abilityId) ?? { items: { item_id: abilityId, item_type: 'ability' } };
      // …and the actor's own line for it (a self-directed use — Dodge's emote / incantation), from its warmed voice shard.
      const catalog = peekAudioCatalog();
      const voiceCtx = catalog ? pageRollSoundContext({ encounter_id: encounterId }, catalog) : null;
      const voice = voiceCtx ? actionVoiceTiers(voiceCtx, { by: row as Row, used: def, seed: command.operation_id }) : null;
      sound = pickActionSound([{ slot: 'cast', tiers: audioTiers(def, 'cast', { taxonomy: catalog?.taxonomy ?? null }) }], command.operation_id, voice);
      if (sound) (command as unknown as Row).sound = sound;
    }
  } catch (error) {
    console.warn('action-truth: action sound pick failed (the row keeps its server route)', error);
  }
  // Ownership boundary: from here the click belongs to the TS lane. The outbox drops an entry whose
  // optimistic apply threw; we undo whatever that apply managed and report the failure locally.
  bindActionTiming(timing, command.operation_id);
  try {
    outbox.enqueue(withLogParticipants(command));
    if (sound) void playActionSound(command.operation_id, sound);
  } catch (error) {
    const own = pending.get(command.operation_id);
    pending.delete(command.operation_id);
    if (own) { revert(row as Row, own.statusId ? [own.statusId] : [], own.aps, own.apsUsed, command.expected_rev); render(command, row); }
    if (typeof displayWarning === 'function') displayWarning('Dodge could not be committed: ' + (error instanceof Error ? error.message : String(error)));
    window.dispatchEvent(new CustomEvent('ft:action-truth-refused', { detail: { command, info: { error: String(error), status: 0, terminal: true } } }));
    return 'refused';
  }
  noteOwnOperation(command.operation_id, SELF_STATUS_SCHEMA);
  return 'handled';
  } finally { endActionTiming(timing); }
}

/** 2026-10-08 (websocket/held-target-move.ts): work that waits on ONE self-status operation's answer — `accepted` once the save is
 *  confirmed, `dropped` when it is answered stale (another page acted on the actor first — the per-actor revision) or refused. */
const selfStatusSettled = new Map<string, { accepted: (revived: boolean) => void; dropped: () => void; proposed: string }>();
function settleSelfStatus(operationId: string, ok: boolean, savedId = ''): void {
  const work = selfStatusSettled.get(operationId);
  if (!work) return;
  selfStatusSettled.delete(operationId);
  try { if (ok) work.accepted(savedId !== '' && savedId !== work.proposed); else work.dropped(); } catch (error) { console.warn('action-truth: settled work failed', error); }
}
/**
 * 2026-10-08 (held-target-move.ts — owner: the front end decides from state it holds, the backend lazy-saves): put an engine MARKER status
 * on the actor exactly the way Dodge puts its own — a self status that ends at the start of the actor's next turn, no AP, no layers —
 * applied to the row at once (every page hears the provisional frame) and saved by action_truth_commit_inc.php under the actor's revision
 * CAS, so a second page acting in the same instant is answered stale and its marker reverted. `connectedId` names the marker (the turn).
 * Returns the operation id (null = the page does not hold the row). `settled` runs once the save is confirmed / dropped; `revived` = the
 * save landed on a row the actor ALREADY held (the statuses unique key target + item) rather than a new one.
 */
export function commitSelfMarkerStatus(input: { encounterId: string; actorId: string; markerId: string; connectedId: string;
  settled?: { accepted: (revived: boolean) => void; dropped: () => void } }): string | null {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), markerId = upper(input.markerId);
  const encounter = actorEncounterFor(encounterId), row = actorRowFor(encounterId, actorId);
  if (!encounter || !row) return null;
  confirmedRevFor(encounterId, actorId, row);
  const ec = asRow(row.encounter_creatures) ?? {};
  const aps = Number(encounterCreatureField(ec, 'aps') ?? 0), apsTotal = Number(encounterCreatureField(ec, 'aps_total') ?? 3);
  if (!Number.isInteger(aps)) return null;
  const apsUsed = Math.max(0, apsTotal - aps);
  const statusId = crypto.randomUUID().toUpperCase(), connectedId = upper(input.connectedId);
  const status = planSelfStatusSave({ statusId, itemId: markerId, actorId, connectedId }, row as Row,
    { item_layer_types: { effect_ends_on_start_of_caster_next_turn: 1 } }, readers(row as Row, encounter));
  const command: ActionTruthCommand = {
    schema: SELF_STATUS_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: encounterId, actor_id: actorId,
    expected_rev: Number(encounterCreatureField(ec, 'movement_rev') ?? 0), ability_id: markerId, roll_private: 0, before: { aps },
    writes: { encounter_creatures: {}, statuses_insert: [status.row], log: { key: 'ability_applied', ability_id: markerId } },
    frame: { statuses_target_entry: status.entry as never, aps, aps_used: apsUsed > 0 ? apsUsed : null, skills: {} },
  };
  if (input.settled) selfStatusSettled.set(command.operation_id, { ...input.settled, proposed: statusId });
  if (selfStatusSettled.size > 128) selfStatusSettled.delete(selfStatusSettled.keys().next().value!);
  try { actorOutbox(encounterId, actorId).enqueue(withLogParticipants(command) as unknown as ActorTruthCommandBase); }
  catch (error) {
    selfStatusSettled.delete(command.operation_id);
    const own = pending.get(command.operation_id);
    pending.delete(command.operation_id);
    if (own) { revert(row as Row, own.statusId ? [own.statusId] : [], null, null, command.expected_rev); render(command as unknown as ActorTruthCommandBase, row); }
    throw error;
  }
  noteOwnOperation(command.operation_id, SELF_STATUS_SCHEMA);
  return command.operation_id;
}

type ActionTruthWireFrame = ActorTruthCommandBase & { ability_id: string; rev: number; frame: ActionTruthFrame; provisional?: boolean; committed?: boolean; correction?: boolean; remove_status_ids?: string[] };
/** Publish-first subscriber (slice-0 semantics): provisional frames render but never bank; committed
 * frames advance confirmed; a correction wins at an equal revision and REVERTS the operation's own
 * effects while adopting the authoritative AP it carries. */
export function handleActionTruth(command: ActionTruthWireFrame): void {
  if (command?.schema !== SELF_STATUS_SCHEMA || !Number.isInteger(command.rev) || command.rev < 0) return;
  const kind = command.correction ? 'correction' : command.committed ? 'committed' : 'provisional';
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const frame = asRow(command.frame);
  if (!row || !frame || !Number.isInteger(frame.aps)) return;
  // Shared dedup + settlement (movement-truth `seen`): a provisional for an operation whose committed / correction frame
  // already arrived — or one this browser owns — never renders again (Codex M1c review A3).
  if (!noteFrameSeen(command.operation_id, kind)) return;
  // A peer hears the actor's picked take on the FIRST frame of the operation it sees (once per operation).
  if (kind !== 'correction') void playActionSound(command.operation_id, (command as unknown as Row).sound, { peer: true });
  const abilityId = upper(command.ability_id);
  const encounter = actorEncounterFor(command.encounter_id);
  if (kind === 'provisional') {
    // Speculative, forward-only, shared with movement (Codex M1c review A2, finding 5); it never stamps the row's
    // revision — only saved truth does.
    if (!admitProvisional(command.encounter_id, command.actor_id, row, command.rev)) return;
    remember(command, row as Row, frame as unknown as ActionTruthFrame, command.rev);
    applyActionTruth(row as Row, frame as unknown as ActionTruthFrame, undefined, definitionOrFetch(row as Row, encounter, abilityId, command));
    render(command, row);
    return;
  }
  // Saved truth: bank the AP it carries FIRST, per field (an A1 frame older than an out-of-combat movement frame still
  // confirms its AP — Codex M1c review A2, finding 4); this is idempotent for our own committed frame too, so a lost
  // HTTP answer never leaves the confirmed AP behind.
  const banked = confirmedRevFor(command.encounter_id, command.actor_id, row);
  bankConfirmedRev(command.encounter_id, command.actor_id, row, command.rev, { aps: Number(frame.aps) });
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  const laterPending = newerPending(command, command.rev);
  let nativeScalars: ActionTruthFrame | undefined;
  if (kind === 'correction') {
    // The operation's own effects go regardless of ordering (a late correction still names what to drop).
    const ids = removalIds(row as Row, command, own, command.remove_status_ids ?? []);
    revert(row as Row, ids, null, null);
  } else {
    stampEntry(row as Row, asRow(frame.statuses_target_entry), ownStatusIds(command, own));
    // A first terminal frame can be late too. Missing entries at an older revision may have expired;
    // replaying that frame's skills would also erase effects of newer confirmed/provisional actions.
    if (command.rev >= banked && command.rev >= Number(row.encounter_creatures.movement_rev ?? 0) && !laterPending) {
      applyActionTruth(row as Row, frame as unknown as ActionTruthFrame, undefined, definitionOrFetch(row as Row, encounter, abilityId, command));
      nativeScalars = frame as unknown as ActionTruthFrame;
    }
  }
  // Shared scalars + revision come from the merged snapshot (a movement frame may hold a newer AP) — unless this
  // browser's own optimistic work is pending on the row: then the overlay stays and the outbox's `settled` hook
  // reconciles once the queue drains (Codex M1c review A3).
  if (!laterPending && pendingFor(command.encounter_id, command.actor_id) === 0) restoreSharedScalars(command, row, nativeScalars);
  render(command, row);
  queueMicrotask(() => settleSelfStatus(command.operation_id, kind === 'committed', upper(frame.statuses_target_entry && asRow(frame.statuses_target_entry)?.status_id)));
}

Object.assign(window, { ftTryCommitSelfStatus: tryCommitSelfStatus });
